import React from "react";
import {
  ArcSegment,
  GaugeThreshold,
  describeAnnularArc,
  describeRoundedAnnularArc,
  defaultColors,
} from "./gaugeUtils";

export interface DialRendererProps {
  // Geometry
  viewWidth: number;
  viewHeight: number;
  cx: number;
  cy: number;
  outerR: number;
  innerR: number;

  // Data
  value: number;
  displayValue: number;
  min: number;
  max: number;
  hasValue: boolean;

  // Pre-computed segments (computed by dispatcher)
  arcSegments: ArcSegment[];
  needleOuterPath: string;
  needleInnerPath: string;

  // Colors (resolved — no CSS variables)
  resolvedColors: string[];
  resolvedThresholds: GaugeThreshold[];
  needleColor: string;
  valueColor: string;

  // Styling
  segmentGap: number;
  roundedSegments: boolean;
  segmentOpacity: number;
  showRangeLabels: boolean;

  // Display
  label: string;
  unit: string;
  valueFontSize: number;
  labelFontSize: number;

  // Accessibility
  ariaLabel?: string;
}

/**
 * Pure dial (semicircular) gauge renderer.
 * No hooks, no state, no DOM access — receives all data as props.
 */
export const DialRenderer: React.FC<DialRendererProps> = ({
  viewWidth,
  viewHeight,
  cx,
  cy,
  outerR,
  innerR,
  value,
  displayValue,
  min,
  max,
  hasValue,
  arcSegments,
  needleOuterPath,
  needleInnerPath,
  resolvedColors,
  resolvedThresholds,
  needleColor,
  valueColor,
  segmentGap: _segmentGap,
  roundedSegments,
  segmentOpacity,
  showRangeLabels,
  label,
  unit,
  valueFontSize,
  labelFontSize,
  ariaLabel,
}) => {
  const hasThresholds = resolvedThresholds.length > 0;
  const computedAriaLabel = ariaLabel
    || (label ? `${label}: ${hasValue ? displayValue : 'no data'}${unit ? ' ' + unit : ''}` : `Gauge: ${hasValue ? displayValue : 'no data'}`);
  const compactValueFontSize = `clamp(${Math.max(9, Math.round(valueFontSize * 0.62))}px, 6.2cqw, ${valueFontSize}px)`;
  const compactUnitFontSize = `clamp(${Math.max(6, Math.round(valueFontSize * 0.36))}px, 3.6cqw, ${Math.round(valueFontSize * 0.625)}px)`;
  const compactLabelFontSize = `clamp(${Math.max(6, Math.round(labelFontSize * 0.68))}px, 3.4cqw, ${labelFontSize}px)`;

  return (
    <div
      data-testid="gauge-dial-renderer"
      role="progressbar"
      aria-valuenow={hasValue ? value : undefined}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={computedAriaLabel}
      style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', containerType: 'inline-size' }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        data-testid="gauge-svg"
      >
        {/* Arc segments */}
        {(() => {
          const useRounded = roundedSegments && hasThresholds;
          if (useRounded) {
            const trackColor = resolvedColors[1] || defaultColors[1];
            const thresholdSegs = arcSegments.filter(seg => seg.color !== trackColor);

            return (
              <>
                {/* No gray track — gaps are transparent */}
                {thresholdSegs.map((seg, i) => {
                  if (Math.abs(seg.startAngle - seg.endAngle) < 0.01) return null;
                  return (
                    <path
                      key={`arc-${i}`}
                      d={describeRoundedAnnularArc(
                        cx, cy, innerR, outerR,
                        seg.startAngle, seg.endAngle,
                        true, true, 7
                      )}
                      fill={seg.color}
                      stroke="none"
                      opacity={segmentOpacity}
                    >
                      {seg.label && <title>{seg.label}</title>}
                    </path>
                  );
                })}
              </>
            );
          }

          // Default: two-segment gauge (value + track)
          // Round only the outer extremity ends (left end of first, right end of last)
          return arcSegments.map((seg, i) => {
            if (Math.abs(seg.startAngle - seg.endAngle) < 0.01) return null;
            const isFirst = i === 0;
            const isLast = i === arcSegments.length - 1;
            const useCornerRounding = isFirst || isLast;
            return (
              <path
                key={`arc-${i}`}
                d={useCornerRounding
                  ? describeRoundedAnnularArc(
                      cx, cy, innerR, outerR,
                      seg.startAngle, seg.endAngle,
                      isFirst, isLast, 7
                    )
                  : describeAnnularArc(cx, cy, innerR, outerR, seg.startAngle, seg.endAngle)
                }
                fill={seg.color}
                stroke="none"
                opacity={segmentOpacity}
              >
                {seg.label && <title>{seg.label}</title>}
              </path>
            );
          });
        })()}

        {/* Needle */}
        <g data-testid="gauge-needle">
          <path d={needleOuterPath} fill={needleColor} stroke={needleColor} strokeWidth="2" strokeLinejoin="round" />
          <path d={needleInnerPath} fill={needleColor} stroke={needleColor} strokeWidth="2" strokeLinejoin="round" />
        </g>

        {/* Min/Max range labels at arc endpoints */}
        {showRangeLabels && (
          <g data-testid="gauge-range-labels">
            <text x={cx - outerR} y={cy + 10} textAnchor="middle" fontSize="10" fill="#9CA3AF" fontWeight="500">
              {min}
            </text>
            <text x={cx + outerR} y={cy + 10} textAnchor="middle" fontSize="10" fill="#9CA3AF" fontWeight="500">
              {max}
            </text>
          </g>
        )}
      </svg>

      {/* Value display overlay */}
      <div
        data-testid="gauge-value-display"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -10%)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: compactValueFontSize, fontWeight: '700', color: valueColor }}>
          {hasValue ? displayValue : '--'}{hasValue && unit && <span style={{ fontSize: compactUnitFontSize, marginLeft: '2px' }}>{unit}</span>}
        </div>
        <div style={{ fontSize: compactLabelFontSize, color: valueColor, fontWeight: '600', marginTop: '4px' }}>
          {label}
        </div>
      </div>
    </div>
  );
};
